import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../../common/SafeIcon';

const { FiBookOpen, FiAlertTriangle, FiCheck, FiInfo, FiPlusCircle } = FiIcons;

const TextComplexityAnalysis = ({ questions }) => {
  const [complexityData, setComplexityData] = useState({});
  const [outliers, setOutliers] = useState([]);
  const [averageReadability, setAverageReadability] = useState({});

  useEffect(() => {
    if (!questions.length) return;
    
    // Calculate readability metrics for each question
    const readabilityScores = questions.map(question => {
      const scores = calculateReadabilityScores(question.text);
      return {
        id: question.id,
        text: question.text.substring(0, 80) + (question.text.length > 80 ? '...' : ''),
        subject: question.subject,
        type: question.type,
        fleschKincaid: scores.fleschKincaid,
        fleschReading: scores.fleschReading,
        gunningFog: scores.gunningFog,
        averageGradeLevel: (scores.fleschKincaid + scores.gunningFog) / 2
      };
    });
    
    // Calculate averages
    const avgFleschKincaid = readabilityScores.reduce((sum, item) => sum + item.fleschKincaid, 0) / readabilityScores.length;
    const avgFleschReading = readabilityScores.reduce((sum, item) => sum + item.fleschReading, 0) / readabilityScores.length;
    const avgGunningFog = readabilityScores.reduce((sum, item) => sum + item.gunningFog, 0) / readabilityScores.length;
    const avgGradeLevel = readabilityScores.reduce((sum, item) => sum + item.averageGradeLevel, 0) / readabilityScores.length;
    
    setAverageReadability({
      fleschKincaid: avgFleschKincaid.toFixed(1),
      fleschReading: avgFleschReading.toFixed(1),
      gunningFog: avgGunningFog.toFixed(1),
      averageGradeLevel: avgGradeLevel.toFixed(1)
    });
    
    // Find outliers (questions with significantly higher or lower complexity)
    const stdDevGradeLevel = Math.sqrt(
      readabilityScores.reduce((sum, item) => 
        sum + Math.pow(item.averageGradeLevel - avgGradeLevel, 2), 0) / readabilityScores.length
    );
    
    const outlierQuestions = readabilityScores.filter(item => 
      Math.abs(item.averageGradeLevel - avgGradeLevel) > (1.5 * stdDevGradeLevel)
    ).sort((a, b) => Math.abs(b.averageGradeLevel - avgGradeLevel) - Math.abs(a.averageGradeLevel - avgGradeLevel));
    
    setOutliers(outlierQuestions.slice(0, 5));
    
    // Group by grade level ranges for visualization
    const gradeLevelDistribution = {};
    readabilityScores.forEach(item => {
      const gradeLevel = Math.floor(item.averageGradeLevel);
      gradeLevelDistribution[gradeLevel] = (gradeLevelDistribution[gradeLevel] || 0) + 1;
    });
    
    setComplexityData({
      readabilityScores,
      gradeLevelDistribution
    });
    
  }, [questions]);

  // Function to calculate readability scores
  const calculateReadabilityScores = (text) => {
    // Count sentences, words, syllables
    const sentences = text.split(/[.!?]+/).filter(Boolean).length || 1;
    const words = text.split(/\s+/).filter(Boolean).length || 1;
    const syllables = countSyllables(text);
    const complexWords = countComplexWords(text);
    
    // Calculate Flesch-Kincaid Grade Level
    const fleschKincaid = 0.39 * (words / sentences) + 11.8 * (syllables / words) - 15.59;
    
    // Calculate Flesch Reading Ease
    const fleschReading = 206.835 - 1.015 * (words / sentences) - 84.6 * (syllables / words);
    
    // Calculate Gunning Fog Index
    const gunningFog = 0.4 * ((words / sentences) + 100 * (complexWords / words));
    
    return {
      fleschKincaid: Math.max(0, Math.min(18, fleschKincaid)),
      fleschReading: Math.max(0, Math.min(100, fleschReading)),
      gunningFog: Math.max(0, Math.min(18, gunningFog))
    };
  };

  // Function to count syllables (simplified)
  const countSyllables = (text) => {
    const words = text.toLowerCase().split(/\s+/).filter(Boolean);
    let count = 0;
    
    words.forEach(word => {
      // Count vowel groups as syllables
      const syllableCount = word.replace(/[^aeiouy]+/g, ' ').trim().split(/\s+/).length;
      count += syllableCount || 1;
    });
    
    return count;
  };

  // Function to count complex words (words with 3+ syllables)
  const countComplexWords = (text) => {
    const words = text.toLowerCase().split(/\s+/).filter(Boolean);
    let count = 0;
    
    words.forEach(word => {
      const syllableCount = word.replace(/[^aeiouy]+/g, ' ').trim().split(/\s+/).length;
      if (syllableCount >= 3) {
        count++;
      }
    });
    
    return count;
  };

  // Get grade level description
  const getGradeLevelDescription = (gradeLevel) => {
    if (gradeLevel < 6) return "Elementary";
    if (gradeLevel < 9) return "Middle School";
    if (gradeLevel < 13) return "High School";
    if (gradeLevel < 16) return "College";
    return "Graduate";
  };

  // Get color class based on grade level
  const getGradeLevelColor = (gradeLevel) => {
    if (gradeLevel < 6) return "bg-green-500";
    if (gradeLevel < 9) return "bg-blue-500";
    if (gradeLevel < 13) return "bg-yellow-500";
    if (gradeLevel < 16) return "bg-orange-500";
    return "bg-red-500";
  };

  // Get chart options for grade level distribution
  const getGradeDistributionOptions = () => {
    if (!complexityData.gradeLevelDistribution) return {};
    
    const grades = Object.keys(complexityData.gradeLevelDistribution).sort((a, b) => a - b);
    const counts = grades.map(grade => complexityData.gradeLevelDistribution[grade]);
    
    return {
      tooltip: {
        trigger: 'axis',
        formatter: '{b} Grade Level: {c} questions'
      },
      xAxis: {
        type: 'category',
        data: grades.map(grade => `${grade}`),
        name: 'Grade Level',
        axisLabel: {
          rotate: 0
        }
      },
      yAxis: {
        type: 'value',
        name: 'Number of Questions'
      },
      series: [
        {
          data: counts,
          type: 'bar',
          name: 'Questions',
          itemStyle: {
            color: function(params) {
              const grade = parseInt(grades[params.dataIndex]);
              if (grade < 6) return '#10B981'; // green
              if (grade < 9) return '#3B82F6'; // blue
              if (grade < 13) return '#FBBF24'; // yellow
              if (grade < 16) return '#F97316'; // orange
              return '#EF4444'; // red
            }
          }
        }
      ]
    };
  };

  if (!questions.length) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 text-center">
        <SafeIcon icon={FiInfo} className="mx-auto text-4xl text-gray-300 mb-4" />
        <h3 className="text-xl font-medium text-gray-800 mb-2">No Questions Available</h3>
        <p className="text-gray-600">
          Add questions to your question bank to view text complexity analysis.
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Decorative gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white/80 backdrop-blur-sm rounded-xl shadow-xl border border-slate-100 p-8 relative z-10 overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-lg">
            <SafeIcon icon={FiBookOpen} className="text-white text-xl" />
          </div>
          <div>
            <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">Text Complexity Analysis</h2>
            <p className="text-slate-600">Assessing the readability and difficulty level of your questions</p>
          </div>
        </div>
        
        {/* Key metrics cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-blue-50 to-cyan-50 p-5 rounded-xl shadow-md border border-blue-100"
          >
            <h3 className="text-sm text-blue-800 font-medium mb-2">Average Grade Level</h3>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-blue-700">{averageReadability.averageGradeLevel || '0.0'}</span>
              <span className="ml-2 text-sm text-blue-600">grade</span>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              {getGradeLevelDescription(averageReadability.averageGradeLevel)}
            </p>
          </motion.div>
          
          <motion.div
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-purple-50 to-indigo-50 p-5 rounded-xl shadow-md border border-purple-100"
          >
            <h3 className="text-sm text-purple-800 font-medium mb-2">Flesch-Kincaid</h3>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-purple-700">{averageReadability.fleschKincaid || '0.0'}</span>
              <span className="ml-2 text-sm text-purple-600">grade</span>
            </div>
            <p className="text-xs text-purple-600 mt-2">
              Grade level based on sentence & word complexity
            </p>
          </motion.div>
          
          <motion.div
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-pink-50 to-rose-50 p-5 rounded-xl shadow-md border border-pink-100"
          >
            <h3 className="text-sm text-pink-800 font-medium mb-2">Flesch Reading Ease</h3>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-pink-700">{averageReadability.fleschReading || '0.0'}</span>
              <span className="ml-2 text-sm text-pink-600">score</span>
            </div>
            <p className="text-xs text-pink-600 mt-2">
              {parseFloat(averageReadability.fleschReading) > 70 ? 'Easy to read' : 
               parseFloat(averageReadability.fleschReading) > 50 ? 'Fairly readable' : 'Difficult reading'}
            </p>
          </motion.div>
          
          <motion.div
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-xl shadow-md border border-amber-100"
          >
            <h3 className="text-sm text-amber-800 font-medium mb-2">Gunning Fog Index</h3>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-amber-700">{averageReadability.gunningFog || '0.0'}</span>
              <span className="ml-2 text-sm text-amber-600">score</span>
            </div>
            <p className="text-xs text-amber-600 mt-2">
              Years of formal education needed to understand
            </p>
          </motion.div>
        </div>
        
        {/* Grade level distribution chart */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Grade Level Distribution</h3>
          <div className="bg-white rounded-xl shadow-md p-4 border border-slate-100">
            <div className="h-72">
              <ReactECharts 
                option={getGradeDistributionOptions()} 
                style={{ height: '100%', width: '100%' }}
              />
            </div>
            <div className="flex justify-center space-x-4 mt-4">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-1"></div>
                <span className="text-xs text-slate-600">Elementary</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-blue-500 mr-1"></div>
                <span className="text-xs text-slate-600">Middle School</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-yellow-500 mr-1"></div>
                <span className="text-xs text-slate-600">High School</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-orange-500 mr-1"></div>
                <span className="text-xs text-slate-600">College</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-red-500 mr-1"></div>
                <span className="text-xs text-slate-600">Graduate</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Outliers */}
        {outliers.length > 0 && (
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <SafeIcon icon={FiAlertTriangle} className="text-amber-500" />
              <h3 className="text-lg font-semibold text-slate-800">Complexity Outliers</h3>
            </div>
            <div className="space-y-3">
              {outliers.map((item, index) => (
                <motion.div
                  key={item.id || index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-4 rounded-xl shadow-md border border-slate-100"
                >
                  <div className="flex items-start space-x-3">
                    <div className={`flex-shrink-0 w-1.5 self-stretch rounded-full ${
                      item.averageGradeLevel > parseFloat(averageReadability.averageGradeLevel)
                        ? 'bg-gradient-to-b from-red-500 to-orange-500' 
                        : 'bg-gradient-to-b from-green-500 to-emerald-500'
                    }`}></div>
                    <div>
                      <div className="flex items-center mb-1 space-x-2">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${
                          item.averageGradeLevel > parseFloat(averageReadability.averageGradeLevel)
                            ? 'bg-red-100 text-red-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          Grade Level: {item.averageGradeLevel.toFixed(1)}
                        </span>
                        <span className="text-xs text-slate-500">
                          {item.averageGradeLevel > parseFloat(averageReadability.averageGradeLevel)
                            ? 'Higher complexity than average'
                            : 'Lower complexity than average'}
                        </span>
                      </div>
                      <p className="text-slate-700 text-sm">{item.text}</p>
                      <div className="flex items-center mt-2 space-x-2 text-xs">
                        <span className="bg-slate-100 px-2 py-0.5 rounded">{item.subject}</span>
                        <span className="text-slate-500">{item.type}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
        
        {/* Readability scale */}
        <div className="mt-8 bg-slate-50 p-5 rounded-xl border border-slate-100">
          <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center">
            <SafeIcon icon={FiInfo} className="mr-2 text-blue-600" />
            Readability Guide
          </h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-4">
              <div className="w-24 h-2 bg-gradient-to-r from-green-500 to-red-500 rounded-full"></div>
              <div className="flex justify-between w-full text-xs text-slate-600">
                <span>Elementary (1-5)</span>
                <span>Middle School (6-8)</span>
                <span>High School (9-12)</span>
                <span>College (13-16)</span>
                <span>Graduate (17+)</span>
              </div>
            </div>
            <p className="text-sm text-slate-600">
              The grade level indicates the years of education typically required to understand the text.
              For most educational assessments, aim for text complexity appropriate to your target audience,
              typically 1-2 grade levels below their current level for optimal comprehension.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TextComplexityAnalysis;